/**
 * Agendamentos.
 *
 * Dois caminhos de criação, propositalmente diferentes:
 *  - o barbeiro marca (`createAppointmentSchema`): escolhe cliente, pode ajustar
 *    a duração na hora e encaixa por cima de quem quiser;
 *  - o cliente pede (`requestAppointmentSchema`): não escolhe duração — ela vem
 *    da ficha dele — e o horário precisa estar na lista de disponíveis.
 *
 * Um atendimento tem **um ou mais serviços**, e o tempo dele é a soma do que
 * cada um leva *para aquele cliente*. Cabelo e barba do João são 25 + 15
 * porque é isso que está na ficha dele, e é assim que um dia em que ele venha
 * só para a barba continua certo.
 */

import { z } from 'zod';
import {
  durationMinutesSchema,
  MAX_SERVICES_PER_APPOINTMENT,
  priceCentsSchema,
} from './catalog.js';
import { idSchema, instantSchema, queryBooleanSchema } from './common.js';
import type { AppointmentPhotoView } from './media.js';
import type { AppointmentSeriesView } from './recurrence.js';

export const appointmentStatusSchema = z.enum([
  'REQUESTED',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'REJECTED',
  'CANCELED_BY_CLIENT',
  'CANCELED_BY_SHOP',
  'NO_SHOW',
]);

export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

export const STATUS_LABELS: Record<AppointmentStatus, string> = {
  REQUESTED: 'Aguardando confirmação',
  CONFIRMED: 'Confirmado',
  IN_PROGRESS: 'Em atendimento',
  COMPLETED: 'Concluído',
  REJECTED: 'Recusado',
  CANCELED_BY_CLIENT: 'Cancelado pelo cliente',
  CANCELED_BY_SHOP: 'Cancelado pela barbearia',
  NO_SHOW: 'Faltou',
};

export const paymentMethodSchema = z.enum(['CASH', 'PIX', 'DEBIT', 'CREDIT', 'OTHER']);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  CASH: 'Dinheiro',
  PIX: 'Pix',
  DEBIT: 'Débito',
  CREDIT: 'Crédito',
  OTHER: 'Outro',
};

// ── Criação ─────────────────────────────────────────────────────────────────

/**
 * Um serviço dentro do atendimento, do jeito que a tela manda.
 *
 * A duração é por item, e não uma só para o atendimento inteiro: é ela que o
 * barbeiro corrige quando diz "hoje a barba dele vai demorar mais", e é ela que
 * `saveDurationForClient` grava na ficha. Um total único não teria como ser
 * gravado — não há como saber de qual serviço veio a diferença.
 */
export const appointmentServiceInputSchema = z.object({
  serviceId: idSchema,
  /** Ausente = usa a duração efetiva do par cliente+serviço. */
  durationMinutes: durationMinutesSchema.optional(),
});

export const appointmentServicesSchema = z
  .array(appointmentServiceInputSchema)
  .min(1, 'Escolha ao menos um serviço')
  .max(MAX_SERVICES_PER_APPOINTMENT, 'Serviços demais para um atendimento só')
  .refine(
    (items) => new Set(items.map((item) => item.serviceId)).size === items.length,
    'O mesmo serviço aparece duas vezes',
  );

/** O barbeiro marcando na agenda dele. */
export const createAppointmentSchema = z.object({
  clientId: idSchema,
  /** Na ordem em que o barbeiro vai fazer; o primeiro é o principal. */
  services: appointmentServicesSchema,
  startsAt: instantSchema,
  /** Ausente = o próprio barbeiro autenticado. */
  membershipId: idSchema.optional(),
  notes: z.string().trim().max(1000).optional(),
  /**
   * Grava as durações informadas como o padrão deste cliente.
   *
   * Vale item a item: marcar isto num atendimento de cabelo e barba com os dois
   * tempos corrigidos ensina os dois pares de uma vez, e cada um continua
   * valendo sozinho no dia em que ele vier só para um deles.
   */
  saveDurationForClient: z.boolean().default(false),
  /**
   * O barbeiro encaixa por cima de outro atendimento conscientemente. Sem esta
   * confirmação a API recusa e devolve o conflito, para a tela avisar antes.
   */
  acceptConflict: z.boolean().default(false),
});

/** O cliente pedindo um horário pelo app. */
export const requestAppointmentSchema = z.object({
  /** Ele escolhe o que fazer, nunca quanto tempo leva. */
  serviceIds: z
    .array(idSchema)
    .min(1, 'Escolha ao menos um serviço')
    .max(MAX_SERVICES_PER_APPOINTMENT, 'Serviços demais para um atendimento só')
    .refine((ids) => new Set(ids).size === ids.length, 'O mesmo serviço aparece duas vezes'),
  startsAt: instantSchema,
  membershipId: idSchema.optional(),
  notes: z.string().trim().max(500).optional(),
});

// ── Alterações ──────────────────────────────────────────────────────────────

export const rescheduleAppointmentSchema = z.object({
  startsAt: instantSchema,
  durationMinutes: durationMinutesSchema.optional(),
  /**
   * A lista inteira de serviços, quando ela muda. Ausente = fica como está.
   *
   * Chega inteira, e não como "tire este, ponha aquele": o barbeiro está
   * olhando a lista na tela e mandando o que ficou nela. Uma diferença
   * calculada no caminho seria uma segunda verdade sobre a mesma tela.
   */
  services: appointmentServicesSchema.optional(),
  membershipId: idSchema.optional(),
  /**
   * O barbeiro encaixa por cima de outro atendimento conscientemente. Sem esta
   * confirmação, a API recusa e devolve o conflito para a tela avisar.
   */
  acceptConflict: z.boolean().default(false),
});

export const changeStatusSchema = z.object({
  status: appointmentStatusSchema,
  reason: z.string().trim().max(300).optional(),
});

/**
 * A resposta do barbeiro a um pedido do cliente.
 *
 * Confirmar não é só dizer "sim": quase sempre o barbeiro quer puxar o horário
 * quinze minutos ou dar mais tempo àquele cliente. Fazer isso em duas etapas —
 * confirmar e depois remarcar — geraria dois avisos ao cliente para uma decisão
 * só, e o segundo chegaria como se a barbearia tivesse mudado de ideia.
 */
export const respondToRequestSchema = z.object({
  decision: z.enum(['CONFIRM', 'REJECT']),
  /** Ausente = mantém o horário que o cliente pediu. */
  startsAt: instantSchema.optional(),
  durationMinutes: durationMinutesSchema.optional(),
  /**
   * Os serviços com que o pedido é confirmado. Ausente = os que ele pediu.
   *
   * É o "hoje só dá para o cabelo" dito no ato de confirmar, e não em uma
   * segunda operação: o cliente recebe um aviso só, com o que de fato vai
   * acontecer. O que ele pediu continua guardado, e a tela dele destaca a
   * diferença.
   */
  services: appointmentServicesSchema.optional(),
  /** Recado para o cliente ler. Vale tanto na confirmação quanto na recusa. */
  note: z.string().trim().max(500).optional(),
  /** Confirma o encaixe por cima de outro atendimento. */
  acceptConflict: z.boolean().default(false),
});

export type RespondToRequestInput = z.infer<typeof respondToRequestSchema>;

export const completeAppointmentSchema = z.object({
  priceCents: priceCentsSchema.optional(),
  paymentMethod: paymentMethodSchema.optional(),
  actualStartAt: instantSchema.optional(),
  actualEndAt: instantSchema.optional(),
});

export const listAppointmentsSchema = z.object({
  /** Dia da agenda, no calendário da barbearia. */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  from: instantSchema.optional(),
  to: instantSchema.optional(),
  membershipId: idSchema.optional(),
  clientId: idSchema.optional(),
  status: z.array(appointmentStatusSchema).optional(),
  includeClosed: queryBooleanSchema.default(true),
});

// ── Leitura ─────────────────────────────────────────────────────────────────

export interface AppointmentView {
  id: string;
  status: AppointmentStatus;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  notes: string | null;
  priceCents: number | null;
  paymentMethod: PaymentMethod | null;
  createdByKind: 'CLIENT' | 'STAFF' | 'SYSTEM';
  actualStartAt: string | null;
  actualEndAt: string | null;
  client: {
    id: string;
    name: string;
    phone: string;
    totalNoShows: number;
  };
  /**
   * O serviço principal — o primeiro da lista.
   *
   * É o que dá cor ao bloco na linha do tempo e nome curto às listas. Quem
   * mostra o atendimento por inteiro lê `services`.
   */
  service: {
    id: string;
    name: string;
    color: string;
    priceCents: number;
  };
  /** Todos os serviços, na ordem em que o barbeiro vai fazer. */
  services: AppointmentServiceView[];
  staff: {
    id: string;
    name: string;
  };
  /** Minutos de invasão em outro atendimento. A linha do tempo destaca. */
  overlapMinutes: number;

  /** Recado que a barbearia escreveu ao responder. O cliente lê. */
  staffNote: string | null;

  /**
   * O que o cliente pediu originalmente, quando o pedido partiu dele.
   * Preenchido mesmo quando nada mudou — é a referência da comparação.
   */
  requested: {
    startsAt: string;
    durationMinutes: number;
    /** Os nomes como estavam no dia do pedido, inclusive os que saíram. */
    services: Array<{ id: string; name: string }>;
  } | null;

  /**
   * O que a barbearia mudou em relação ao pedido.
   *
   * `null` quando não houve pedido do cliente ou nada mudou. Quando existe, a
   * tela do cliente destaca: aceitar sem perceber que o horário mudou é a
   * forma mais fácil de alguém perder o corte.
   */
  adjusted: {
    time: boolean;
    duration: boolean;
    /** A barbearia tirou ou trocou algum serviço do pedido. */
    services: boolean;
  } | null;

  /**
   * Fotos do atendimento, da mais antiga para a mais recente.
   *
   * Para a equipe vêm todas; para o cliente, só as que o barbeiro liberou. É a
   * rota que filtra — a tela nunca recebe uma foto que não devia mostrar e
   * decide escondê-la, porque essa é a forma de vazamento que ninguém percebe.
   */
  photos: AppointmentPhotoView[];

  /**
   * O horário fixo que gerou este atendimento, se foi um.
   *
   * O cliente vê também: é o que explica a ele por que existe uma terça
   * marcada que ele não pediu esta semana.
   */
  series: AppointmentSeriesView | null;
}

/** Um serviço dentro do atendimento, com o tempo e o preço daquele dia. */
export interface AppointmentServiceView {
  id: string;
  name: string;
  color: string;
  /** Minutos deste cliente neste serviço, congelados na marcação. */
  durationMinutes: number;
  /** Preço de tabela no dia em que foi marcado, não o de hoje. */
  priceCents: number;
}

export interface DayAgendaView {
  date: string;
  timezone: string;
  /**
   * Sobreposição que a barbearia aceita, em minutos.
   *
   * A tela usa para separar o encaixe que o dono autorizou ("10 min, dentro da
   * sua tolerância") do que estoura o combinado. Sem isso, todo encaixe viraria
   * alerta e o alerta perderia o sentido.
   */
  overlapToleranceMinutes: number;
  /** Faixas de trabalho do dia, para a linha do tempo saber onde começa e acaba. */
  workingRanges: Array<{ start: string; end: string }>;
  /**
   * Os horários da grade fixa neste dia ("09:00", "09:40"…), e o passo entre
   * eles. Vazio quando a barbearia usa a grade encadeada — aí a linha do tempo
   * oferece os vãos livres, que é o que existe para oferecer.
   */
  grid: { times: string[]; stepMinutes: number };
  blocks: Array<{ id: string; startsAt: string; endsAt: string; reason: string | null }>;
  appointments: AppointmentView[];
  /** Quantos pedidos aguardam resposta — o crachá do cabeçalho. */
  pendingCount: number;
}

/**
 * Um dia resumido a números, para as visões de semana e de mês.
 *
 * Desenhar cada atendimento numa grade de sete colunas em tela de 390px seria
 * ilegível. O barbeiro não precisa ler os nomes ali — precisa ver onde há
 * espaço e onde está apertado.
 */
export interface DayLoadView {
  date: string;
  /** Minutos de expediente já descontadas as folgas. Zero = não atende. */
  workingMinutes: number;
  bookedMinutes: number;
  freeMinutes: number;
  /** 0 a 1. */
  occupancy: number;
  closed: boolean;
  appointments: number;
  /** Pedidos aguardando resposta neste dia. */
  pending: number;
  firstStartsAt: string | null;
  lastEndsAt: string | null;
}

// ── Eventos em tempo real (SSE) ─────────────────────────────────────────────

export type AgendaEventType =
  | 'APPOINTMENT_REQUESTED'
  | 'APPOINTMENT_CHANGED'
  | 'APPOINTMENT_CANCELED';

export interface AgendaEvent {
  type: AgendaEventType;
  appointmentId: string;
  membershipId: string;
  /** Dia afetado, para a tela decidir se precisa recarregar. */
  date: string;
  at: string;
}
